import React, { useMemo, useRef, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from './popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from './command';
import { cn } from '@/lib/utils';

export function MultiSelectDropdown({
  options = [],
  value = [],
  onChange,
  placeholder = 'Select...',
  disabled = false,
  className = '',
  error = false,
}) {
  const [open, setOpen] = useState(false);
  const contentRef = useRef(null);
  const selectedValues = Array.isArray(value) ? value : [];

  const selectedLabels = useMemo(
    () =>
      options
        .filter((option) => selectedValues.includes(option.value))
        .map((option) => option.label),
    [options, selectedValues]
  );

  const triggerLabel =
    selectedLabels.length > 0 ? selectedLabels.join(', ') : placeholder;

  const handlePointerDownOutside = (e) => {
    if (contentRef.current && contentRef.current.contains(e.target)) {
      e.preventDefault();
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          className={cn(
            'flex h-9 w-full items-center justify-between rounded-md border border-input bg-transparent px-3 py-1 text-left text-base shadow-sm transition-colors',
            'placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-gray-800 focus:border-gray-400',
            'data-[state=open]:border-gray-400 data-[state=open]:ring-1 data-[state=open]:ring-gray-800 data-[state=open]:outline-none',
            'disabled:cursor-not-allowed disabled:opacity-50 md:text-sm',
            error && 'border-red-500',
            className
          )}
        >
          <span className={cn('truncate', selectedLabels.length === 0 && 'text-muted-foreground')}>
            {triggerLabel}
          </span>
          <ChevronDown className="h-4 w-4 shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        ref={contentRef}
        className="w-[var(--radix-popover-trigger-width)] min-w-[16rem] max-w-[24rem] p-0 rounded-lg shadow-lg border border-border bg-popover"
        align="start"
        onPointerDownOutside={handlePointerDownOutside}
        onInteractOutside={(e) => {
          if (contentRef.current && contentRef.current.contains(e.target)) {
            e.preventDefault();
          }
        }}
      >
        <Command shouldFilter={true} className="rounded-lg overflow-hidden">
          <CommandInput placeholder="Search..." />
          <CommandList>
            <CommandEmpty>No result found.</CommandEmpty>
            <CommandGroup>
              {options.map((option) => {
                const selected = selectedValues.includes(option.value);
                return (
                  <CommandItem
                    key={option.value}
                    value={option.label}
                    onSelect={() => {
                      const nextValues = selected
                        ? selectedValues.filter((item) => item !== option.value)
                        : [...selectedValues, option.value];
                      onChange(nextValues);
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={selected}
                      readOnly
                      className="mr-2 h-4 w-4 rounded border-gray-300"
                    />
                    <span>{option.label}</span>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
